import { useEffect, useState } from "react";
import { getAgent, runAgent, buildAgent, fetchAgentRunStreamingResult, streamAgentRunResult } from "../api/agentApi";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Alert, Button, Snackbar, TextField, Accordion, AccordionSummary, AccordionDetails, Toolbar, Divider, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { marked } from "marked";

const POLL_INTERVAL_BASE = 5000; // ms
const POLL_INTERVAL_MAX = 60000; // ms

export default function AgentDetailPage() {
  const handleRun = async () => {
    setRunStatus(null);
    setIsRunning(true);
    setStreamedOutput("");
    setIsStreaming(false);
    try {
      const result = await runAgent(
        agentId,
        JSON.stringify({ input: prompt }) + "\n",
        openaiApiKey
      );
      setRunStatus(result);
      setSnackbarOpen(true);
      // Refresh agent after successful run to update number_of_executions
      if (result.status === "success") {
        const updated = await getAgent(agentId);
        setAgent(updated);
      }
    } catch (e) {
      setRunStatus({ status: "error", message: "Failed to run agent." });
      setSnackbarOpen(true);
    } finally {
      setIsRunning(false);
    }
  };
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [error, setError] = useState("");
  const [runStatus, setRunStatus] = useState(null);
  const [buildStatus, setBuildStatus] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [openaiApiKey, setOpenaiApiKey] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);
  const [streamedOutput, setStreamedOutput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [typewriterSpeed, setTypewriterSpeed] = useState(20); // Default: normal speed
  const [streamConnection, setStreamConnection] = useState(null);

  useEffect(() => {
    let poller;
    let isUnmounted = false;
    const fetchAgent = async () => {
      try {
        const data = await getAgent(agentId);
        if (!isUnmounted) {
          setAgent(data);
          setError("");
          // Only poll every 5s if status is 'creating' or 'building'
          if (data.status === "creating" || data.status === "building") {
            poller = setTimeout(fetchAgent, POLL_INTERVAL_BASE);
          }
          // No polling for other statuses
        }
      } catch {
        if (!isUnmounted) setError("Failed to load agent details.");
      }
    };
    fetchAgent();
    return () => {
      isUnmounted = true;
      if (poller) clearTimeout(poller);
    };
  }, [agentId]);

  useEffect(() => {
    let connection;
    if (runStatus && runStatus.streaming_result_endpoint) {
      setIsStreaming(true);
      setStreamedOutput("");
      // Start streaming with typewriter effect
      const { streamAgentRunResult } = require("../api/agentApi");
      connection = streamAgentRunResult(
        runStatus.streaming_result_endpoint,
        chunk => setStreamedOutput(chunk),
        err => {
          setStreamedOutput(prev => {
            if (prev.length === 0 || (connection && connection.eventSource && connection.eventSource.readyState !== 2)) {
              return prev + "\n[Error streaming result]";
            }
            return prev;
          });
          // Do not set isStreaming to false here; wait for animation end
        },
        {
          typewriterSpeed,
          onAnimationEnd: () => setIsStreaming(false)
        }
      );
      setStreamConnection(connection);
      connection.eventSource.onopen = () => setIsStreaming(true);
      // Remove onclose handler; handled by onAnimationEnd now
    }
    return () => {
      if (connection) connection.cleanup();
      setStreamConnection(null);
    };
  }, [runStatus && runStatus.streaming_result_endpoint]);

  const handleBuild = async () => {
    setBuildStatus(null);
    setIsBuilding(true);
    // Immediately set agent status to 'building' for UI feedback and polling
    setAgent(prev => prev ? { ...prev, status: "building" } : prev);
    setError(""); // Clear any previous error
    // Start polling immediately after setting status
    const poller = setTimeout(async function poll() {
      try {
        const data = await getAgent(agentId);
        setAgent(data);
        if (data.status === "building") {
          setTimeout(poll, POLL_INTERVAL_BASE);
        }
      } catch {
        setError("Failed to load agent details.");
      }
    }, POLL_INTERVAL_BASE);
    try {
      const result = await buildAgent(agentId);
      setBuildStatus(result);
      setSnackbarOpen(true);
    } catch (e) {
      setBuildStatus({ status: "error", message: "Failed to build agent." });
      setSnackbarOpen(true);
    } finally {
      setIsBuilding(false);
    }
  };

  useEffect(() => {
    let connection;
    if (runStatus && runStatus.streaming_result_endpoint) {
      setIsStreaming(true);
      setStreamedOutput("");
      
      // Start streaming with typewriter effect
      connection = streamAgentRunResult(
        runStatus.streaming_result_endpoint,
        chunk => setStreamedOutput(chunk), // Now receives progressive text
        err => {
          setStreamedOutput(prev => prev + "\n[Error streaming result]");
          setIsStreaming(false);
        },
        { typewriterSpeed } // Use initial speed value
      );
      
      // Store connection for cleanup and speed updates
      setStreamConnection(connection);
      
      // Handle connection open/close
      connection.eventSource.onopen = () => setIsStreaming(true);
      connection.eventSource.onclose = () => setIsStreaming(false);
    }
    return () => {
      if (connection) connection.cleanup();
      setStreamConnection(null);
    };
  }, [runStatus && runStatus.streaming_result_endpoint]); // Only reconnect when endpoint changes
  
  // Update speed when typewriterSpeed state changes
  useEffect(() => {
    if (streamConnection && streamConnection.updateSpeed) {
      streamConnection.updateSpeed(typewriterSpeed);
    }
  }, [typewriterSpeed, streamConnection]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!agent) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;

  // Use agent object directly (API now returns JSON)
  const card = agent;

  const handleEdit = () => {
    // Always pass the latest card as state for pre-population
    navigate("/agent/conversational", { state: { agentCard: card } });
  };

  const extractOutput = (rawString) => {
    // 1. Remove ANSI escape codes
    const ansiRegex = /\x1B\[[0-?]*[ -/]*[@-~]/g;
    const noAnsi = rawString.replace(ansiRegex, '');

    // 2. Match 'output': '...' OR "output": "..."
    //    The `(["'])` captures either single or double quote and `\1` enforces matching same type
    const match = noAnsi.match(/['"]output['"]\s*:\s*(["'])([\s\S]*?)\1\s*\}/);

    if (!match) {
      throw new Error("Could not find 'output' in string");
    }

    // 3. Extract the captured value
    let output = match[2];

    // 4. Unescape common escaped sequences
    output = output
      .replace(/\\n/g, '\n')   // Newlines
      .replace(/\\"/g, '"')    // Escaped double quotes
      .replace(/\\'/g, "'");   // Escaped single quotes

    return output.trim();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Toolbar disableGutters sx={{ display: "flex", alignItems: "center"}}>
        <Typography variant="h4" sx={{ pl: 0 }}>Agent: {agent.name}</Typography>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" color="primary" onClick={handleEdit} sx={{ mr: 2 }}>
          Edit
        </Button>
        <Button variant="contained" color="secondary" onClick={handleBuild} disabled={isBuilding || (card.status === "building")} sx={{ mr: 2 }}> 
          {isBuilding || card.status === "building" ? "Building..." : "Build Agent"}
        </Button>
        <Button component={Link} to="/agents" variant="outlined">
          Back to Agent List
        </Button>
      </Toolbar>
      <Divider sx={{ mb: 3 }} />
      <Box component={Paper} elevation={3} sx={{ mx: "auto", p: 4 }}>
        {/* List style agent info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "#f9f9f9", borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Role</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>{card.role}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Agent ID</Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>{card.agent_id}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Description</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>{card.description}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Date</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>{card.date || ""}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Owner</Typography>
          <Typography variant="body2">{card.owner || "Unknown"}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Status</Typography>
          <Box sx={{
            px: 2,
            py: 0.5,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: "1rem",
            color: card.status === "ready" ? "#fff" : "#333",
            bgcolor: card.status === "ready"
              ? "#43a047"
              : card.status === "building"
                ? "#ffb300"
                : card.status === "created"
                  ? "#1976d2"
                  : "#e0e0e0",
            boxShadow: card.status === "ready" ? 2 : 0,
            display: "inline-block",
            minWidth: 160,
            textAlign: "center",
            mb: 2,
            textTransform: "capitalize"
          }}>
            {card.status || "unverified"}
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Number of Executions</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>{card.number_of_executions ?? 0}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Tools</Typography>
          {Array.isArray(card.tools) && card.tools.length > 0 ? (
            <Box sx={{ mb: 2, pl: 2 }}>
              {card.tools.map((tool, idx) => (
                <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{tool.name || tool.tool_name || `Tool ${idx + 1}`}</Typography>
                  {tool.description && (
                    <Typography variant="caption" sx={{ color: '#666' }}>{tool.description}</Typography>
                  )}
                  {tool.type && (
                    <Typography variant="caption" sx={{ color: '#888', ml: 1 }}>Type: {tool.type}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ mb: 2, color: '#888' }}>No tools found for this agent.</Typography>
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>LLM</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>{card.llm}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>LLM Settings</Typography>
          <Box sx={{ mb: 2, pl: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <pre style={{ margin: 0, fontSize: '0.95em' }}>{JSON.stringify(card.llm_settings, null, 2)}</pre>
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Message</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>{card.message || ""}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Associated Collections</Typography>
          {Array.isArray(card.rag) && card.rag.length > 0 ? (
            <Box sx={{ mb: 2, pl: 2 }}>
              {card.rag.map((index, idx) => (
                <Box key={index.id || idx} sx={{ mb: 1, p: 1, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {index.name || `Index ${idx + 1}`}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    ID: {index.id}
                  </Typography>
                  {index.rag_type && (
                    <Typography variant="caption" sx={{ color: '#888', ml: 1 }}>
                      Type: {index.rag_type}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ mb: 2, color: '#888' }}>No collections associated with this agent.</Typography>
          )}
        </Box>
        {/* Build status messages */}
        {buildStatus && buildStatus.status === "started" && card.status === "building" && (
          <Alert severity="info" sx={{ mt: 2 }}>Agent build has started. Status: Building...</Alert>
        )}
        {card.status === "building" && !buildStatus?.status === "error" && !buildStatus?.status === "success" && (
          <Alert severity="info" sx={{ mt: 2 }}>Agent build is in progress. Please wait...</Alert>
        )}
        {card.status === "ready" && (
          <Alert severity="success" sx={{ mt: 2 }}>Agent is now ready for execution!</Alert>
        )}
        {card.status === "failed" && (
          <Alert severity="error" sx={{ mt: 2 }}>Agent build failed: {card.message}</Alert>
        )}
        <TextField
          label="Prompt"
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 3, bgcolor: card.status !== "ready" ? '#f0f0f0' : undefined }}
          placeholder='Describe your task here...'
          //disabled={card.status !== "ready"}
        />
        {/* <TextField
          label="OpenAI API Key (optional)"
          value={openaiApiKey}
          onChange={e => setOpenaiApiKey(e.target.value)}
          fullWidth
          sx={{ mt: 2 }}
          placeholder="sk-..."
          type="password"
        /> */}
        <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleRun} /*disabled={isRunning || card.status !== "ready"}*/>
          {isRunning ? 'Running...' : 'Run Agent'}
        </Button>
        {isRunning && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        {isStreaming && (
          <Box sx={{ mt: 2, background: '#e3f2fd', p: 2, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Streaming Result</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="typewriter-speed-label">Animation Speed</InputLabel>
                  <Select
                    labelId="typewriter-speed-label"
                    value={typewriterSpeed}
                    label="Animation Speed"
                    onChange={(e) => setTypewriterSpeed(e.target.value)}
                  >
                    <MenuItem value={0}>Instant</MenuItem>
                    <MenuItem value={10}>Fast</MenuItem>
                    <MenuItem value={20}>Normal</MenuItem>
                    <MenuItem value={50}>Slow</MenuItem>
                  </Select>
                </FormControl>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={() => streamConnection?.skipAnimation()}
                  disabled={!streamConnection}
                >
                  Show All
                </Button>
              </Box>
            </Box>
            <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
              {streamedOutput.length === 0 && <CircularProgress size={24} sx={{ mr: 2 }} />}
              {/* Format streamed output: render newlines as <br>, preserve whitespace, and escape HTML */}
              <span dangerouslySetInnerHTML={{
                __html: streamedOutput
                  .replace(/&/g, "&amp;")
                  .replace(/</g, "&lt;")
                  .replace(/>/g, "&gt;")
                  .replace(/\n/g, "<br>")
              }} />
            </Box>
          </Box>
        )}
        {!isStreaming && streamedOutput && (
          <Box sx={{ mt: 2, background: '#e3f2fd', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Streaming Result</Typography>
            <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
              <span dangerouslySetInnerHTML={{
                __html: streamedOutput
                  .replace(/&/g, "&amp;")
                  .replace(/</g, "&lt;")
                  .replace(/>/g, "&gt;")
                  .replace(/\n/g, "<br>")
              }} />
            </Box>
          </Box>
        )}
        {runStatus && runStatus.status === "success" && runStatus.output && (
          <Box sx={{ mt: 2, background: '#e3f2fd', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Result</Typography>
            <Box
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}
              component="div"
              dangerouslySetInnerHTML={{
                __html: (() => {
                  try {
                    return marked.parse(extractOutput(runStatus.output));
                  } catch (e) {
                    return "<i>Please look at complete result.</i>";
                  }
                })()
              }}
            />
          </Box>
        )}
        {runStatus && runStatus.output && (
          <Accordion sx={{ mt: 2, background: '#e3f2fd', borderRadius: 1, boxShadow: 'none' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Output (raw agent output)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                {runStatus.output}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
        {runStatus && runStatus.status === "error" && (runStatus.message || runStatus.output) && (
          <Accordion sx={{ mt: 2, background: '#ffebee', borderRadius: 1, boxShadow: 'none' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#b71c1c' }}>Error (click to expand)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, color: '#b71c1c' }}>
                {runStatus.message}
                {runStatus.output && (typeof runStatus.output === 'string' ? `\n${runStatus.output}` : '')}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
        {buildStatus && buildStatus.status === "error" && card.status !== "building" && (
          <Accordion sx={{ mt: 2, background: '#ffebee', borderRadius: 1, boxShadow: 'none' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Build Error
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                {buildStatus.output || buildStatus.message}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
        {buildStatus && buildStatus.status === "success" && (
          <Accordion sx={{ mt: 2, background: '#e3f2fd', borderRadius: 1, boxShadow: 'none' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Build Output
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                {buildStatus.output || buildStatus.message}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message={
            (buildStatus && buildStatus.status === "started" && "Agent build started.") ||
            (buildStatus && buildStatus.status === "success" && "Agent built successfully.") ||
            (buildStatus && buildStatus.status === "error" && card.status !== "building" && buildStatus.message) ||
            (runStatus && runStatus.status === "success"
              ? `Agent running. Container ID: ${runStatus.container_id}`
              : runStatus && runStatus.status === "error" && runStatus.message)
          }
        />
      </Box>
    </Box>
  );
}